import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Printer, Download, PenLine, PackageCheck, Trash2, Upload, Pencil, Mail, ReceiptText, BadgeDollarSign } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail, uploadDocument } from "@/lib/api";
import { printDeliveryOrder, downloadDeliveryOrderPdf } from "@/lib/deliveryOrderPrint";
import { shopCarriersMailto } from "@/lib/shopCarriers";
import { DO_STATUS_META, doStatusMeta, doStatusOptions } from "@/pages/DeliveryOrders";

const num = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
const fmtLong = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const cfgOf = (products, l) => (products.find((p) => p.id === l.product_id)?.pack_configs || []).find((c) => c.id === l.config_id);

const shippedFor = (dv, l) => {
  const bl = dv?.byLine?.[l.id] || dv?.byLine?.[`${l.product_id}|${l.design_id || ""}|${l.config_id || ""}`];
  return bl ? (bl.shipped || 0) + (bl.planned || 0) : 0;
};

export function GenerateBolDialog({ so, products, customers, onOpenChange, onSaved }) {
  const [rows, setRows] = useState([]);
  const [shipAddressId, setShipAddressId] = useState("");
  const [saving, setSaving] = useState(false);

  const customer = useMemo(() => customers.find((c) => c.id === so?.customer_id) || null, [customers, so]);
  const addrs = customer?.addresses || [];

  useEffect(() => {
    if (!so) return;
    setRows((so.lines || []).map((l) => {
      const cfg = cfgOf(products, l);
      const remaining = Math.max(0, (l.cases || 0) - shippedFor(so._dv, l));
      return {
        line: l, cfg,
        cpp: l.cases_per_pallet || Number(cfg?.cases_per_pallet) || null,
        remaining,
        checked: remaining > 0,
        qty: String(remaining || 0),
      };
    }));
    setShipAddressId(addrs.length === 1 ? addrs[0].id : "");
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [so]);

  if (!so) return null;

  const allChecked = rows.every((r) => r.checked || r.remaining <= 0);
  const anySelectable = rows.some((r) => r.remaining > 0);
  const toggleAll = (on) => setRows((prev) => prev.map((r) => ({ ...r, checked: on && r.remaining > 0 })));
  const setRow = (i, patch) => setRows((prev) => prev.map((r, x) => (x === i ? { ...r, ...patch } : r)));

  const selected = rows.filter((r) => r.checked && Number(r.qty) > 0);
  const totals = selected.reduce((a, r) => {
    const c = Number(r.qty) || 0;
    a.cases += c;
    if (r.cpp) a.pallets += c / r.cpp;
    return a;
  }, { cases: 0, pallets: 0 });

  const generate = async () => {
    if (!selected.length) { toast.error("Select at least one line with a quantity"); return; }
    if (addrs.length > 1 && !shipAddressId) { toast.error("This customer has multiple addresses — pick the delivery address for the BOL"); return; }
    setSaving(true);
    try {
      const dims = (cfg) => {
        const d = [cfg?.pallet_length, cfg?.pallet_width, cfg?.pallet_height];
        return d.some((v) => Number(v) > 0) ? d.map((v) => (Number(v) > 0 ? Number(v).toLocaleString() : "?")).join(" × ") + " in" : "";
      };
      const { data } = await api.post("/delivery-orders", {
        so_id: so.id, status: "open",
        ship_address_id: shipAddressId,
        pickup_date: so.delivery_date || "", rdd: so.ship_date || "", ship_from: so.ship_from || "",
        lines: selected.map(({ line: l, cfg, cpp, qty }) => ({
          so_line_id: l.id, product_id: l.product_id, product_name: l.product_name,
          design_id: l.design_id || "", design_name: l.design_name || "",
          config_id: l.config_id || "", case_pack: l.case_pack,
          cases: Number(qty),
          cases_per_pallet: cpp,
          pallets_per_truckload: Number(cfg?.pallets_per_truckload) || null,
          weight_per_case: Number(cfg?.weight_per_case) || null,
          weight_per_pallet: Number(cfg?.weight_per_pallet) || null,
          pallet_dims: dims(cfg),
        })),
      });
      toast.success(`${data.bol_number || data.do_number} created for ${so.so_number}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to generate BOL");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!so} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="generate-bol-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Generate BOL — {so.so_number}</DialogTitle>
          <DialogDescription>Pick the lines and quantities going on this Bill of Lading. The rest of the order stays open for future BOLs.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 border border-slate-200 bg-slate-50 p-3 text-xs sm:grid-cols-3">
          <div><p className="font-semibold uppercase tracking-wide text-slate-400">Customer</p><p className="text-slate-800">{so.customer_name}</p></div>
          <div><p className="font-semibold uppercase tracking-wide text-slate-400">Sales Order #</p><p className="text-slate-800">{so.so_number}</p></div>
          <div><p className="font-semibold uppercase tracking-wide text-slate-400">Customer PO #</p><p className="text-slate-800">{so.customer_po || "—"}</p></div>
          <div><p className="font-semibold uppercase tracking-wide text-slate-400">Requested Delivery</p><p className="text-slate-800">{fmtLong(so.ship_date)}</p></div>
          <div className="col-span-2"><p className="font-semibold uppercase tracking-wide text-slate-400">Ship To</p>
            <p className="whitespace-pre-line text-slate-800">{(so.ship_to || "").trim() || [customer?.address_line1, customer?.address_line2].filter(Boolean).join("\n") || so.customer_name}</p></div>
        </div>

        {addrs.length > 1 && (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Delivery address for this BOL</p>
            <Select value={shipAddressId} onValueChange={setShipAddressId}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="bol-ship-address"><SelectValue placeholder="Select address…" /></SelectTrigger>
              <SelectContent>
                {addrs.map((a) => (<SelectItem key={a.id} value={a.id}>{a.label || a.address}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="border border-slate-200">
          <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <Checkbox checked={anySelectable && allChecked} disabled={!anySelectable} onCheckedChange={(v) => toggleAll(!!v)} data-testid="bol-select-all" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">{allChecked && anySelectable ? "Deselect all" : "Select all"}</span>
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">BOL Cases</span>
          </div>
          {rows.map((r, i) => {
            const l = r.line;
            const shippedAlready = (l.cases || 0) - r.remaining;
            const pallets = r.cpp ? Number(r.qty || 0) / r.cpp : null;
            return (
              <div key={l.id} className={`flex items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0 ${r.remaining <= 0 ? "opacity-50" : ""}`} data-testid={`bol-line-${l.id}`}>
                <Checkbox checked={r.checked} disabled={r.remaining <= 0} onCheckedChange={(v) => setRow(i, { checked: !!v })} data-testid={`bol-line-check-${l.id}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-slate-800">
                    {l.product_name}{l.design_name ? ` | ${l.design_name}` : ""}{l.case_pack ? ` | ${num(l.case_pack)}-Pack` : ""}
                  </p>
                  <p className="text-xs text-slate-400">
                    Ordered {num(l.cases)} cases{r.cpp ? ` / ${num((l.cases || 0) / r.cpp)} pallets` : ""}
                    {shippedAlready > 0 && <span className="ml-1.5 text-orange-600">{r.remaining > 0 ? `· ${num(r.remaining)} remaining` : "· fully on BOLs"}</span>}
                  </p>
                </div>
                <div className="text-right">
                  <Input type="number" min="0" max={r.remaining} value={r.qty} disabled={!r.checked || r.remaining <= 0}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(Number(e.target.value) || 0, r.remaining));
                      setRow(i, { qty: e.target.value === "" ? "" : String(v) });
                    }}
                    className="h-8 w-24 rounded-sm text-right tabular-nums" data-testid={`bol-line-qty-${l.id}`} />
                  {pallets != null && r.checked && <p className="mt-0.5 text-[10px] tabular-nums text-slate-400">{num(pallets)} pallets</p>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs tabular-nums text-slate-500" data-testid="bol-totals">
            {num(totals.cases)} cases{totals.pallets ? ` · ${num(totals.pallets)} pallets` : ""} on this BOL
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="bol-cancel">Cancel</Button>
            <Button size="sm" className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={saving || !selected.length} onClick={generate} data-testid="bol-generate">
              {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FileText className="mr-1.5 h-3.5 w-3.5" />} Generate BOL
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const DOC_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt";
const SQ_BTN = "flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border transition-colors";

export function SoDocButton({ so, kind, icon: Icon, label, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const docs = so[`${kind}_docs`] || [];
  const upload = async (f) => {
    setBusy(true);
    try {
      const d = await uploadDocument(f);
      await api.post(`/sales-orders/${so.id}/docs`, { kind, name: d.name || f.name, url: d.url, path: d.path });
      toast.success(`${label} attached to ${so.so_number}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };
  const dragProps = {
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); },
    onDragEnter: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); },
    onDragLeave: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation(); setDragOver(false);
      if (busy) return;
      const f = e.dataTransfer?.files?.[0];
      if (f) upload(f);
      else toast.error("Drop a file — save the attachment first if dragging from an email preview");
    },
  };
  const remove = async (idx) => {
    try {
      await api.delete(`/sales-orders/${so.id}/docs/${kind}/${idx}`);
      toast.success(`${label} removed`);
      onChanged?.();
    } catch { toast.error("Failed to remove document"); }
  };
  const fileInput = (
    <input type="file" className="hidden" accept={DOC_ACCEPT}
      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
  );
  if (!docs.length) {
    return (
      <label title={`Upload ${label} — click or drag a file onto this square`} onClick={(e) => e.stopPropagation()} {...dragProps}
        className={`${SQ_BTN} cursor-pointer border-dashed ${dragOver ? "border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-slate-300 text-slate-400 hover:border-[#002FA7] hover:text-[#002FA7]"}`}
        data-testid={`so-${kind}-upload-${so.id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
        {fileInput}
      </label>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button title={`${label} — ${docs.length} file${docs.length !== 1 ? "s" : ""} (drop a file to add another)`} onClick={(e) => e.stopPropagation()} {...dragProps}
          className={`${SQ_BTN} relative ${dragOver ? "border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-500"}`}
          data-testid={`so-${kind}-files-${so.id}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
          {docs.length > 1 && <span className="absolute -right-1 -top-1 rounded-full bg-emerald-600 px-1 text-[9px] font-bold leading-3 text-white">{docs.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-[260px] rounded-sm p-0" onClick={(e) => e.stopPropagation()}>
        <p className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">{label} — {so.so_number}</p>
        {docs.map((d, i) => (
          <div key={`${d.url}-${i}`} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-xs last:border-b-0">
            <a href={d.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-medium text-[#002FA7] hover:underline" data-testid={`so-${kind}-file-link-${so.id}-${i}`}>{d.name}</a>
            <button title="Remove" className="p-1 text-slate-400 hover:text-red-600" onClick={() => remove(i)} data-testid={`so-${kind}-file-del-${so.id}-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <label className="flex cursor-pointer items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-500 hover:text-[#002FA7]">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload another
          {fileInput}
        </label>
      </PopoverContent>
    </Popover>
  );
}

function CarrierRow({ d, carriers, onChanged, multi, shopCtx, soShipTo }) {
  const full = d.full || {};
  const [price, setPrice] = useState(full.freight_price != null ? String(full.freight_price) : "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPrice(full.freight_price != null ? String(full.freight_price) : ""); }, [full.freight_price]);
  const opts = useMemo(() => {
    const set = new Set(carriers || []);
    if (full.carrier) set.add(full.carrier);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [carriers, full.carrier]);
  const save = async (patch) => {
    setBusy(true);
    try {
      await api.patch(`/delivery-orders/${d.id}/carrier`, {
        carrier: patch.carrier !== undefined ? patch.carrier : full.carrier || "",
        freight_price: patch.freight_price !== undefined ? patch.freight_price : (price === "" ? null : parseFloat(price)),
      });
      if (patch.carrier !== undefined) toast.success(`${patch.carrier} assigned to ${full.bol_number || d.do_number}`);
      else toast.success(`Freight price saved on ${full.bol_number || d.do_number}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };
  const savePrice = () => {
    const v = price === "" ? null : parseFloat(price);
    if (price !== "" && (Number.isNaN(v) || v < 0)) { toast.error("Enter a valid price"); return; }
    if (v === (full.freight_price ?? null)) return;
    save({ freight_price: v });
  };
  return (
    <div className="flex flex-col gap-0.5" data-testid={`so-carrier-row-${d.id}`}>
      {multi && <span className="text-[10px] font-semibold text-slate-400">{full.bol_number || d.do_number}</span>}
      <Select value={full.carrier || undefined} onValueChange={(v) => save({ carrier: v })} disabled={busy}>
        <SelectTrigger className={`h-7 w-[140px] rounded-sm text-xs ${full.carrier ? "" : "text-slate-400"}`} data-testid={`so-carrier-select-${d.id}`}>
          <SelectValue placeholder="Assign carrier…" />
        </SelectTrigger>
        <SelectContent>
          {opts.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No carriers — add them under Contacts → Land Freight</div>}
          {opts.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
        </SelectContent>
      </Select>
      {full.carrier && (
        <div className="relative w-[140px]">
          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
          <Input value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
            onBlur={savePrice} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            placeholder="Shipment price" disabled={busy}
            className="h-7 rounded-sm pl-5 text-xs tabular-nums" data-testid={`so-carrier-price-${d.id}`} />
        </div>
      )}
      {shopCtx && (
        <button type="button"
          onClick={async () => {
            const company = await shopCtx.getCompany();
            const { emails, href } = await shopCarriersMailto(full, company, shopCtx.landFreight, shopCtx.customers, shopCtx.products, soShipTo);
            if (!emails.length) toast.warning("No Land Freight contacts with an email found — add them on the Contacts page.");
            window.location.href = href;
          }}
          className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
          title="Draft a freight quote request email to your Land Freight contacts"
          data-testid={`so-shop-carriers-${d.id}`}>
          <Mail className="h-3 w-3" /> Shop Carriers
        </button>
      )}
    </div>
  );
}

export function SoShipmentStatusCell({ so, onChanged }) {
  const dos = so._dv?.dos || [];
  const isPickup = (so.pickup_delivery || "") === "pickup";
  if (!dos.length) {
    if (so.status === "cancelled") return <span className="text-xs text-slate-300">—</span>;
    const open = doStatusMeta("open", isPickup);
    return (
      <span className={`inline-flex items-center rounded-sm px-2 py-1 text-[11px] font-semibold ${open.cls}`}
        title="No BOL created yet — the shipment starts as Open"
        data-testid={`so-shipment-status-default-${so.id}`}>{open.label}</span>
    );
  }
  return (
    <div className="flex flex-col gap-1.5" data-testid={`so-shipment-status-cell-${so.id}`}>
      {dos.map((d) => (<ShipmentStatusRow key={d.id} d={d} multi={dos.length > 1} isPickup={isPickup} onChanged={onChanged} />))}
    </div>
  );
}

function ShipmentStatusRow({ d, multi, isPickup, onChanged }) {
  const [busy, setBusy] = useState(false);
  const cur = doStatusMeta(d.status, isPickup);
  const set = async (v) => {
    setBusy(true);
    try {
      await api.put(`/delivery-orders/${d.id}/status`, { status: v });
      toast.success(`${d.full?.bol_number || d.do_number} → ${doStatusMeta(v, isPickup).label}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Failed to update status"));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5">
      {multi && <span className="max-w-[70px] truncate text-[10px] text-slate-400" title={d.full?.bol_number || d.do_number}>{d.full?.bol_number || d.do_number}</span>}
      <Select value={d.status} onValueChange={set} disabled={busy}>
        <SelectTrigger className={`h-6 w-auto gap-1 rounded-sm border-0 px-2 py-0 text-[11px] font-semibold ${cur.cls}`} data-testid={`so-shipment-status-${d.id}`}>
          <span>{cur.label}</span>
        </SelectTrigger>
        <SelectContent>
          {doStatusOptions(isPickup).map(([k, lbl]) => (<SelectItem key={k} value={k}>{lbl}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function SoCarrierCell({ so, carriers, onChanged, shopCtx }) {
  if ((so.pickup_delivery || "") === "pickup") {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700"
        title="Customer arranges and pays for the carrier on pickup orders"
        data-testid={`so-carrier-customer-paid-${so.id}`}>
        Customer Assigned / Paid
      </span>
    );
  }
  const dos = so._dv?.dos || [];
  const invBtn = (
    <div className="flex items-center gap-2.5">
      <div className="flex items-center gap-1.5" title="Carrier invoice — click or drag the carrier's freight bill here">
        <SoDocButton so={so} kind="carrier_invoice" icon={ReceiptText} label="Carrier Invoice" onChanged={onChanged} />
        <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-slate-400">INV</span>
      </div>
      <div className="flex items-center gap-1.5" title="Proof of payment — click or drag a payment screenshot here">
        <SoDocButton so={so} kind="carrier_payment" icon={BadgeDollarSign} label="Carrier Payment Proof" onChanged={onChanged} />
        <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-slate-400">PMT</span>
      </div>
    </div>
  );
  if (!dos.length) {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-slate-300">—</span>
        {invBtn}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2" data-testid={`so-carrier-cell-inner-${so.id}`}>
      {dos.map((d) => (<CarrierRow key={d.id} d={d} carriers={carriers} onChanged={onChanged} multi={dos.length > 1} shopCtx={shopCtx} soShipTo={so.ship_to || ""} />))}
      {invBtn}
    </div>
  );
}

function BolDeleteBtn({ d, onChanged }) {
  const [arm, setArm] = useState(false);
  const del = async () => {
    try {
      await api.delete(`/delivery-orders/${d.id}`);
      toast.success(`${d.full?.bol_number || d.do_number} deleted${d.full?.stock_moved ? " — stock movement reversed" : ""}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't delete the BOL");
    }
  };
  return arm ? (
    <button title="Click to confirm deletion" onMouseLeave={() => setArm(false)}
      className="rounded-sm bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white hover:bg-red-700"
      onClick={del} data-testid={`so-bol-delete-confirm-${d.id}`}>Delete?</button>
  ) : (
    <button title="Delete BOL" className="p-1 text-slate-400 hover:text-red-600"
      onClick={() => setArm(true)} data-testid={`so-bol-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
  );
}

export function SoBolCell({ so, customers, getCompany, onGenerate, onChanged, onEditBol }) {
  const dos = so._dv?.dos || [];
  const customer = customers.find((c) => c.id === so.customer_id) || null;
  const act = async (fn, d) => {
    const company = await getCompany();
    fn(d.full, company, customer);
  };
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1.5">
        <button title="Generate BOL" onClick={(e) => { e.stopPropagation(); onGenerate(); }}
          className={`${SQ_BTN} border-slate-200 bg-white text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]`}
          data-testid={`so-generate-bol-${so.id}`}>
          <FileText className="h-3.5 w-3.5" />
        </button>
        {dos.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="whitespace-nowrap text-xs font-semibold text-[#002FA7] hover:underline" onClick={(e) => e.stopPropagation()} data-testid={`so-bol-count-${so.id}`}>
                {dos.length} BOL{dos.length !== 1 ? "s" : ""}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto min-w-[340px] rounded-sm p-0" onClick={(e) => e.stopPropagation()}>
              <p className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">BOLs — {so.so_number}</p>
              {dos.map((d) => (
                <div key={d.id} className="flex items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-xs last:border-b-0" data-testid={`so-bol-item-${d.id}`}>
                  <span className="font-semibold text-slate-800">{d.full?.bol_number || d.do_number}</span>
                  {d.full?.seal_number && <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600" title="Seal number" data-testid={`so-bol-seal-${d.id}`}>Seal {d.full.seal_number}</span>}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${doStatusMeta(d.status, (so.pickup_delivery || "") === "pickup").cls}`}>{doStatusMeta(d.status, (so.pickup_delivery || "") === "pickup").label}</span>
                  <span className="tabular-nums text-slate-500">{num(d.cases)} cs</span>
                  {d.pickup_date && <span className="text-slate-400">{fmtLong(d.pickup_date)}</span>}
                  <span className="ml-auto flex gap-1">
                    {onEditBol && d.full && (
                      <button title="Edit BOL" className="p-1 text-slate-500 hover:text-[#002FA7]" onClick={() => onEditBol(d.full)} data-testid={`so-bol-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    )}
                    <button title="View / print" className="p-1 text-slate-500 hover:text-[#002FA7]" onClick={() => act(printDeliveryOrder, d)} data-testid={`so-bol-print-${d.id}`}><Printer className="h-3.5 w-3.5" /></button>
                    <button title="Download PDF" className="p-1 text-slate-500 hover:text-[#002FA7]" onClick={() => act(downloadDeliveryOrderPdf, d)} data-testid={`so-bol-download-${d.id}`}><Download className="h-3.5 w-3.5" /></button>
                    <BolDeleteBtn d={d} onChanged={onChanged} />
                  </span>
                </div>
              ))}
            </PopoverContent>
          </Popover>
        )}
      </div>
      <SoDocButton so={so} kind="signed_bol" icon={PenLine} label="Signed BOL" onChanged={onChanged} />
      <SoDocButton so={so} kind="pod" icon={PackageCheck} label="POD" onChanged={onChanged} />
    </div>
  );
}
